import React, { useState, useEffect } from 'react';
import { MessageSquare, Mail, CheckCircle, Clock, Loader2 } from 'lucide-react';
import { Button } from '../ui/button';
import { toast } from 'sonner';
import api from '../../services/api';

const ContactManagement = () => {
  const [contacts, setContacts] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    loadContacts();
  }, []);

  const loadContacts = async () => {
    setLoading(true);
    try {
      const response = await api.get('/contact');
      setContacts(response.data);
    } catch (error) {
      console.error('Error loading contacts:', error);
      toast.error('Erreur lors du chargement des messages');
    } finally {
      setLoading(false);
    }
  };

  const updateStatus = async (contactId, newStatus) => {
    try {
      const formData = new FormData();
      formData.append('status', newStatus);
      
      await api.put(`/contact/${contactId}/status`, formData);
      
      setContacts(contacts.map(c => 
        c.id === contactId ? { ...c, status: newStatus } : c
      ));
      toast.success('Statut mis à jour');
    } catch (error) {
      console.error('Error updating status:', error);
      toast.error('Erreur lors de la mise à jour');
    }
  };

  const getStatusBadge = (status) => {
    const styles = {
      pending: 'bg-yellow-500/20 text-yellow-300 border-yellow-500/30',
      contacted: 'bg-blue-500/20 text-blue-300 border-blue-500/30',
      resolved: 'bg-green-500/20 text-green-300 border-green-500/30',
    };
    
    const icons = {
      pending: <Clock className="w-3 h-3" />,
      contacted: <Mail className="w-3 h-3" />,
      resolved: <CheckCircle className="w-3 h-3" />,
    };
    
    const labels = {
      pending: 'En attente',
      contacted: 'Contacté',
      resolved: 'Résolu',
    };

    return (
      <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium border ${styles[status] || styles.pending}`}>
        {icons[status]}
        {labels[status] || status}
      </span>
    );
  };

  if (loading) {
    return (
      <div className="flex justify-center items-center h-64">
        <Loader2 className="w-8 h-8 animate-spin text-purple-400" />
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fadeIn">
      <div className="flex justify-between items-center">
        <h2 className="text-2xl font-bold text-white flex items-center gap-2">
          <MessageSquare className="w-6 h-6 text-purple-400" />
          Messages de Contact
        </h2>
        <span className="text-gray-400">
          {contacts.length} message{contacts.length !== 1 ? 's' : ''}
        </span>
      </div>

      <div className="space-y-4">
        {contacts.length === 0 ? (
          <div className="glass rounded-xl p-8 text-center">
            <MessageSquare className="w-12 h-12 text-gray-500 mx-auto mb-3" />
            <p className="text-gray-400">Aucun message de contact</p>
          </div>
        ) : (
          contacts.map((contact) => (
            <div
              key={contact.id}
              className="glass rounded-xl p-6 space-y-4 hover:border-purple-500/30 transition-colors"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h3 className="text-lg font-semibold text-white">
                      {contact.name}
                    </h3>
                    {getStatusBadge(contact.status)}
                  </div>
                  <p className="text-sm text-gray-400 mb-1">
                    <Mail className="w-3 h-3 inline mr-1" />
                    {contact.email}
                  </p>
                  <p className="text-sm text-purple-300 font-medium mb-2">
                    Sujet: {contact.subject}
                  </p>
                  <p className="text-sm text-gray-300 bg-slate-700/30 rounded p-3">
                    {contact.message}
                  </p>
                </div>
              </div>

              <div className="flex items-center justify-between pt-3 border-t border-slate-700">
                <span className="text-xs text-gray-500">
                  {new Date(contact.created_at).toLocaleString('fr-FR')}
                </span>
                <div className="flex gap-2">
                  {contact.status === 'pending' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(contact.id, 'contacted')}
                      className="bg-blue-600 hover:bg-blue-700"
                    >
                      Marquer contacté
                    </Button>
                  )}
                  {contact.status === 'contacted' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(contact.id, 'resolved')}
                      className="bg-green-600 hover:bg-green-700"
                    >
                      Marquer résolu
                    </Button>
                  )}
                  {contact.status === 'resolved' && (
                    <Button
                      size="sm"
                      onClick={() => updateStatus(contact.id, 'pending')}
                      variant="outline"
                      className="border-slate-600"
                    >
                      Réouvrir
                    </Button>
                  )}
                </div>
              </div>
            </div>
          ))
        )}
      </div>
    </div>
  );
};

export default ContactManagement;
